// Team chat on a ticket's discussion (the ticket discussion round, 2026-11-02; brief rule 8, the red team's findings 13 and 23): the room's
// head leads with its ticket ("#123 · Subject · <Project>", a link to the ticket, and the status pill) and offers Follow / Stop following;
// an address `/chat/ticket/<n>` whose room is not among the rooms the column holds (older than its 30 days, or opened from a mail) is found
// through the ticket's own discussion read; the column's Tickets group has its "Show all" at `?tickets=all`.
import { useEffect, useState } from "react";
import { Button, Pill, STATUS_LABEL, type Navigate } from "@omnabase/ui";
import { ticketRoomLabel, type ChatRoom, type TicketStatus } from "@omnabase/shared";
import { fetchDiscussion, followRoom } from "../../lib/chat";
import { currentParams, useParamNow, useSetParam } from "../../lib/nav";
import type { ChatAddress } from "../../lib/chatRooms";

/** the ticket's link and its status, in the room's head */
export function TicketRoomLead({ room, project, href, onNavigate }: { room: ChatRoom; project: string | undefined; href: string; onNavigate: Navigate }) {
  const label = [ticketRoomLabel(room.ticketNumber ?? 0, room.ticketSubject), project].filter(Boolean).join(" · ");
  const status = room.ticketStatus as TicketStatus | null | undefined;
  return (
    <>
      <a className="ob-chat__lead-link" href={href} title={label} onClick={(e) => onNavigate(href, e)}>{label}</a>
      {status && STATUS_LABEL[status] && <Pill>{STATUS_LABEL[status]}</Pill>}
    </>
  );
}

/** Follow / Stop following for the reader alone; the rooms read again afterwards */
export function FollowButton({ slug, room, onDone, onError }: { slug: string; room: ChatRoom; onDone: () => Promise<void>; onError: (e: unknown) => void }) {
  const on = !!room.following;
  return <Button size="sm" onClick={() => void followRoom(slug, room.id, !on).then(onDone, onError)}>{on ? "Stop following" : "Follow"}</Button>;
}

/** A ticket's discussion named by its address but not in the column's list: read by the ticket's number. `undefined` while asking (or not a
 *  ticket address), `null` when there is none for this reader (no such ticket, not theirs to see, or nobody has written yet). */
export function useTicketRoomByAddress(slug: string, address: ChatAddress | null, inList: ChatRoom | null, rooms: ChatRoom[] | null): ChatRoom | null | undefined {
  const number = address?.kind === "ticket" && !inList && rooms ? address.number : null;
  const [found, setFound] = useState<{ number: number; room: ChatRoom | null } | null>(null);
  useEffect(() => {
    if (number === null) return;
    let stale = false;
    void fetchDiscussion(slug, number).then((d) => { if (!stale) setFound({ number, room: d.room }); }, () => { if (!stale) setFound({ number, room: null }); });
    return () => { stale = true; };
  }, [slug, number, rooms]);
  if (number === null) return undefined;
  return found?.number === number ? found.room : undefined;
}

/** the column's Tickets group: whether "Show all" is open (`?tickets=all`, kept on reload) and the switch */
export function useTicketsColumn() {
  const setParam = useSetParam();
  return { all: useParamNow("tickets") === "all", toggle: () => setParam("tickets", currentParams().get("tickets") === "all" ? null : "all") };
}
